import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, RefreshCw, Clock, CheckCircle, AlertCircle, ChevronLeft, Paperclip, Loader2 } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { supportTicketsAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSupport } from '../contexts/SupportContext';

// Image attachment limits
const MAX_ATTACHMENTS = 5;
const TARGET_KB = 300;            // primary compression target
const HARD_CAP_KB = 600;          // backend rejects > 600 KB
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

/**
 * Compress an image to ~300 KB with up to 1600px max dimension while
 * keeping screenshots readable. Falls back to a higher cap if necessary.
 * Always returns a File or throws if it cannot get below the hard cap.
 */
async function compressForSupport(file) {
  const passes = [
    { maxSizeMB: TARGET_KB / 1024, maxWidthOrHeight: 1600, initialQuality: 0.85 },
    { maxSizeMB: HARD_CAP_KB / 1024, maxWidthOrHeight: 1280, initialQuality: 0.78 },
  ];
  let compressed = file;
  for (const p of passes) {
    try {
      compressed = await imageCompression(file, {
        ...p,
        useWebWorker: false,                       // safer in dev preview / sandboxed iframes
        fileType: file.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
      });
    } catch (err) {
      console.warn('[support] compression pass failed:', err);
      throw new Error(`Compression failed: ${err?.message || 'unknown error'}`);
    }
    if (compressed.size / 1024 <= HARD_CAP_KB) break;
  }
  if (compressed.size / 1024 > HARD_CAP_KB) {
    throw new Error(`Could not compress below ${HARD_CAP_KB} KB without making text unreadable.`);
  }
  return compressed;
}

/**
 * <img> wrapper that fetches the attachment via the authenticated `api`
 * client (so the JWT goes in the Authorization header) and renders it from
 * an object URL. Avoids embedding tokens in <img src> query strings.
 */
function AuthedImage({ src, alt, className }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    api.get(src.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);
  if (!blobUrl) {
    return <div className={`${className} animate-pulse bg-black/10`} />;
  }
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" />;
}

const SupportBubble = () => {
  const { isDrawerOpen: isOpen, openDrawer, closeDrawer } = useSupport();
  const [supportStatus, setSupportStatus] = useState({ online: true, hours_text: '', support_enabled: true });
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  
  // Form state
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  // Attachments staged for the next send (shared between create + reply forms;
  // cleared after each successful send).
  // Each item: { id, file (compressed File), preview (object URL),
  //              originalSizeKb, compressedSizeKb, status: 'pending'|'uploading'|'uploaded'|'error',
  //              path?, error? }
  const [attachments, setAttachments] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch support status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await supportTicketsAPI.getStatus();
      setSupportStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch support status:', err);
    }
  }, []);

  // Fetch tickets list only. Keeping this stable (no deps on selectedTicket)
  // prevents a stale-closure race where the in-flight call would reopen a
  // ticket the user has just navigated back from.
  const fetchTickets = useCallback(async () => {
    try {
      const res = await supportTicketsAPI.getMyTickets();
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  }, []);

  // Track the *currently selected* ticket id in a ref so async refreshes can
  // verify the user hasn't navigated away before applying setSelectedTicket.
  const selectedTicketIdRef = useRef(null);
  useEffect(() => {
    selectedTicketIdRef.current = selectedTicket?.id || null;
  }, [selectedTicket?.id]);

  // Refresh the currently-open ticket's detail (messages). Guarded so it
  // never reopens a ticket the user has gone back from.
  const refreshSelectedTicket = useCallback(async () => {
    const id = selectedTicketIdRef.current;
    if (!id) return;
    try {
      const res = await supportTicketsAPI.getTicket(id);
      // Re-check the ref after the await — if the user clicked back or
      // selected a different ticket, drop the stale response.
      if (selectedTicketIdRef.current === id) {
        setSelectedTicket(res.data);
      }
    } catch (err) {
      console.error('Failed to refresh selected ticket:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      fetchTickets();
    }
  }, [isOpen, fetchStatus, fetchTickets]);

  // Polling when open
  useEffect(() => {
    if (isOpen) {
      pollIntervalRef.current = setInterval(() => {
        fetchTickets();
        refreshSelectedTicket();
      }, 15000); // Poll every 15 seconds
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, fetchTickets, refreshSelectedTicket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (selectedTicket?.messages) {
      scrollToBottom();
    }
  }, [selectedTicket?.messages]);

  // ── Attachments ──
  const handleSelectFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    console.log('[support] selected files:', files.map(f => f.name + ' ' + f.size));

    setCompressing(true);
    try {
      for (const file of files) {
        if (!ALLOWED_MIME.includes(file.type.toLowerCase())) {
          toast.error(`'${file.name}' is not a supported image (PNG, JPG, WEBP only)`);
          continue;
        }

        // Compute next-id outside setState so we can use it as a key when uploading
        const tmpId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const originalSizeKb = Math.round(file.size / 1024);

        let compressed;
        try {
          compressed = await compressForSupport(file);
          console.log('[support] compressed', file.name, originalSizeKb, '->', Math.round(compressed.size/1024), 'KB');
        } catch (e) {
          console.error('[support] compression failed:', e);
          toast.error(e.message || 'Failed to compress image');
          continue;
        }
        const compressedSizeKb = Math.round(compressed.size / 1024);
        const preview = URL.createObjectURL(compressed);

        // Capacity guard inside the updater (atomic)
        let added = false;
        setAttachments((prev) => {
          if (prev.length >= MAX_ATTACHMENTS) {
            return prev;
          }
          added = true;
          return [
            ...prev,
            {
              id: tmpId,
              file: compressed,
              originalName: file.name,
              preview,
              originalSizeKb,
              compressedSizeKb,
              status: 'uploading',
            },
          ];
        });
        if (!added) {
          URL.revokeObjectURL(preview);
          toast.error(`Maximum ${MAX_ATTACHMENTS} screenshots per message`);
          break;
        }

        try {
          const res = await supportTicketsAPI.uploadImage(compressed);
          setAttachments((prev) =>
            prev.map((a) => (a.id === tmpId ? { ...a, status: 'uploaded', path: res.data.path } : a)),
          );
          console.log('[support] uploaded', tmpId, '->', res.data.path);
        } catch (e) {
          const detail = e.response?.data?.detail || 'Upload failed';
          setAttachments((prev) =>
            prev.map((a) => (a.id === tmpId ? { ...a, status: 'error', error: String(detail) } : a)),
          );
          toast.error(String(detail));
        }
      }
    } finally {
      setCompressing(false);
    }
  }, []);

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const a = prev.find((x) => x.id === id);
      if (a?.preview) URL.revokeObjectURL(a.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      return [];
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => () => clearAttachments(), [clearAttachments]);

  /**
   * Paste handler: pulls image blobs from the clipboard (Ctrl+V / Cmd+V on a
   * Win+Shift+S or Cmd+Shift+4 screenshot) and feeds them into the same
   * compression+upload pipeline used by the file input.
   */
  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            // Browser-pasted screenshots are usually nameless — give them a sensible name
            const named = new File([f], f.name && f.name !== 'image.png' ? f.name : `pasted-${Date.now()}.png`, {
              type: f.type || 'image/png',
            });
            files.push(named);
          }
        }
      }
      if (files.length > 0) {
        e.preventDefault(); // don't paste raw bytes into the textarea
        handleSelectFiles(files);
      }
    },
    [handleSelectFiles],
  );

  // Get the array of uploaded paths to send with the message
  const uploadedPaths = () => attachments.filter((a) => a.status === 'uploaded' && a.path).map((a) => a.path);
  const hasUploadingAttachment = () => attachments.some((a) => a.status === 'uploading');

  // Renders the staged attachment thumbnails + remove buttons + the attach action
  const AttachmentStrip = ({ inputRef, disabled }) => (
    <div className="space-y-2">
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="support-attachments-strip">
          {attachments.map((a) => (
            <div
              key={a.id}
              className="relative group rounded-md border border-gray-200 bg-white overflow-hidden w-20 h-20"
              data-testid={`support-attachment-${a.id}`}
            >
              <img src={a.preview} alt={a.originalName} className="w-full h-full object-cover" />
              {a.status === 'uploading' && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
              {a.status === 'error' && (
                <div className="absolute inset-0 bg-red-600/70 flex items-center justify-center text-[10px] text-white text-center px-1">
                  failed
                </div>
              )}
              {a.status === 'uploaded' && (
                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                  {a.compressedSizeKb} KB
                </span>
              )}
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                className="absolute -top-1 -right-1 bg-gray-800 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                title="Remove"
                data-testid={`support-attachment-remove-${a.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || compressing || attachments.length >= MAX_ATTACHMENTS}
          data-testid="support-attach-btn"
        >
          <Paperclip className="w-4 h-4" />
          {compressing ? 'Compressing…' : 'Attach screenshot'}
          {attachments.length > 0 && ` (${attachments.length}/${MAX_ATTACHMENTS})`}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_MIME.join(',')}
          multiple
          className="hidden"
          onChange={(e) => {
            handleSelectFiles(e.target.files);
            e.target.value = ''; // allow re-selecting same file
          }}
          data-testid="support-attach-input"
        />
      </div>
    </div>
  );

  // Create new ticket
  const handleCreateTicket = async () => {
    if (!newTicketMessage.trim()) return;
    if (hasUploadingAttachment()) {
      toast.error('Please wait for screenshots to finish uploading');
      return;
    }

    setSending(true);
    try {
      const res = await supportTicketsAPI.createTicket({
        subject: newTicketSubject.trim() || null,
        message: newTicketMessage.trim(),
        attachment_paths: uploadedPaths(),
      });
      
      setTickets([res.data, ...tickets]);
      setSelectedTicket(res.data);
      setNewTicketSubject('');
      setNewTicketMessage('');
      setShowNewTicketForm(false);
      clearAttachments();
      toast.success('Support message sent');
    } catch (err) {
      toast.error('Failed to send message');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // Add reply to ticket
  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    if (hasUploadingAttachment()) {
      toast.error('Please wait for screenshots to finish uploading');
      return;
    }

    setSending(true);
    try {
      await supportTicketsAPI.addMessage(selectedTicket.id, {
        message: replyMessage.trim(),
        attachment_paths: uploadedPaths(),
      });
      
      // Refresh ticket
      const res = await supportTicketsAPI.getTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      setReplyMessage('');
      clearAttachments();
      toast.success('Reply sent');
      
      // Also refresh ticket list
      fetchTickets();
    } catch (err) {
      toast.error('Failed to send reply');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // Close ticket
  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      await supportTicketsAPI.closeTicket(selectedTicket.id);
      toast.success('Ticket closed');
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  // Refresh manually
  const handleRefresh = async () => {
    setLoading(true);
    await fetchTickets();
    await fetchStatus();
    setLoading(false);
  };

  // Select a ticket to view
  const handleSelectTicket = async (ticket) => {
    setLoading(true);
    try {
      const res = await supportTicketsAPI.getTicket(ticket.id);
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Open</Badge>;
      case 'responded':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Responded</Badge>;
      case 'closed':
        return <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">Closed</Badge>;
      default:
        return null;
    }
  };

  // Has open/responded tickets
  const hasActiveTickets = tickets.some(t => t.status !== 'closed');
  const activeTicketCount = tickets.filter(t => t.status !== 'closed').length;

  return (
    <>
      {/* Support Tab - Right Edge (all viewports) */}
      <button
        onClick={openDrawer}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all duration-200 rounded-l-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 ${isOpen ? 'translate-x-full pointer-events-none opacity-0' : 'translate-x-0'}`}
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        data-testid="support-tab"
        aria-label="Open support chat"
        tabIndex={0}
      >
        <div className="flex items-center gap-1 px-2 py-3 md:gap-1.5 md:px-2.5 md:py-4">
          <MessageCircle className="w-4 h-4" />
          <span className="text-xs md:text-sm font-medium md:tracking-wide">Support</span>
          {hasActiveTickets && (
            <span className="w-4 h-4 md:w-5 md:h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold">
              {activeTicketCount}
            </span>
          )}
        </div>
      </button>

      {/* Support Drawer */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ maxHeight: '100dvh' }}
        data-testid="support-drawer"
      >
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b bg-blue-600 text-white">
          <div className="flex items-center gap-3">
            {selectedTicket && (
              <button onClick={() => setSelectedTicket(null)} className="hover:bg-blue-700 p-1 rounded">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <MessageCircle className="w-5 h-5" />
            <h2 className="font-semibold">
              {selectedTicket ? `Ticket #${selectedTicket.id.slice(0, 8)}` : 'Support'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRefresh} className="hover:bg-blue-700 p-1.5 rounded" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={closeDrawer} className="hover:bg-blue-700 p-1.5 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Offline Banner - Fixed when present */}
        {!supportStatus.online && (
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800 flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>Support is currently offline. Hours: {supportStatus.hours_text}</span>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {selectedTicket ? (
            // Ticket Thread View
            <div className="flex flex-col h-full">
              {/* Ticket Info */}
              <div className="p-3 border-b bg-gray-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{selectedTicket.subject || 'No subject'}</span>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <div className="text-xs text-gray-500">
                  Created {new Date(selectedTicket.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.author_type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                        msg.author_type === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid={`msg-attachments-${msg.id}`}>
                          {msg.attachments.map((att) => (
                            <button
                              key={att.id}
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
                                  const url = URL.createObjectURL(res.data);
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                                } catch (e) {
                                  toast.error('Could not open attachment');
                                }
                              }}
                              className="block rounded overflow-hidden border border-white/20 hover:opacity-90 text-left"
                              title={`${att.file_name} (${Math.round(att.size_bytes/1024)} KB)`}
                            >
                              <AuthedImage src={att.url} alt={att.file_name} className="w-full h-24 object-cover bg-black/10" />
                            </button>
                          ))}
                        </div>
                      )}
                      <p className={`text-[10px] mt-1 ${msg.author_type === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>
                        {msg.author_name} • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' ? (
                <form onSubmit={handleAddReply} className="p-3 border-t bg-white space-y-2">
                  <div className="flex gap-2">
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      onPaste={handlePaste}
                      placeholder="Type your message... (paste screenshots with Ctrl+V)"
                      className="flex-1 min-h-[60px] max-h-[120px] resize-none text-sm"
                      data-testid="support-reply-input"
                    />
                    <Button
                      type="submit"
                      disabled={!replyMessage.trim() || sending || hasUploadingAttachment()}
                      className="self-end"
                      data-testid="support-send-reply"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <AttachmentStrip inputRef={replyFileInputRef} disabled={sending} />
                  <button
                    type="button"
                    onClick={handleCloseTicket}
                    className="text-xs text-gray-500 hover:text-gray-700 mt-2"
                  >
                    Close this ticket
                  </button>
                </form>
              ) : (
                <div className="p-3 border-t bg-gray-50 text-center text-sm text-gray-500">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  This ticket is closed
                </div>
              )}
            </div>
          ) : showNewTicketForm || tickets.length === 0 ? (
            // New Ticket Form - with scrollable body and fixed footer
            <div className="flex-1 flex flex-col min-h-0">
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {tickets.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowNewTicketForm(false)}
                    className="text-sm text-blue-600 hover:text-blue-800 mb-4 text-left flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to tickets
                  </button>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject (optional)</label>
                  <Input
                    value={newTicketSubject}
                    onChange={(e) => setNewTicketSubject(e.target.value)}
                    placeholder="Brief description"
                    className="w-full"
                    data-testid="support-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <Textarea
                    value={newTicketMessage}
                    onChange={(e) => setNewTicketMessage(e.target.value)}
                    onPaste={handlePaste}
                    placeholder="How can we help you? (Tip: paste a screenshot with Ctrl+V / Cmd+V)"
                    className="min-h-[150px] resize-none"
                    required
                    data-testid="support-message-input"
                  />
                </div>
                <div className="mt-3">
                  <AttachmentStrip inputRef={fileInputRef} disabled={sending} />
                </div>
              </div>
              
              {/* Fixed Footer */}
              <div className="flex-shrink-0 p-4 border-t bg-white">
                <Button
                  type="button"
                  onClick={handleCreateTicket}
                  disabled={!newTicketMessage.trim() || sending}
                  className="w-full"
                  data-testid="support-submit-btn"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
                
                {!supportStatus.online && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    We&apos;ll respond during business hours
                  </p>
                )}
              </div>
            </div>
          ) : (
            // Ticket List
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 border-b bg-gray-50">
                <p className="text-sm font-medium text-gray-700">Your Tickets</p>
              </div>
              <div className="divide-y">
                {tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm truncate flex-1 mr-2">
                        {ticket.subject || 'No subject'}
                      </span>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>{new Date(ticket.updated_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{ticket.message_count} message{ticket.message_count !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* New Ticket Button */}
              <div className="p-3 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowNewTicketForm(true)}
                  data-testid="new-ticket-btn"
                >
                  Start New Conversation
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          onClick={closeDrawer}
        />
      )}
    </>
  );
};

export default SupportBubble;
